import React, { Fragment, useEffect, useState } from "react";
import { useLocation, withRouter } from "react-router";
import { Box, Container, Fab, Typography, withStyles } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import useUserStateContext from "../../userstate";
import { useGetMe } from "../../fetch/me";
import { stylesCommon, stylesProduct } from "../../css";
import { googleform, salesformUrl } from "../../constants";

type formInfoType = {
    tag: string
    name: string
    url: string
    width: string
    height: string
}

const procedure = `
### ご予約は以下の手順でお進めください。

1. カレンダー上のご希望の日程を選択

2. 表示されたフォームに必要事項を入力（**※メールアドレスはマイページ登録アドレスをご入力ください。**）

3. フォーム上の「予約」ボタンをクリック

4. 予約が確定したら、画面右上の「完了」をクリック（**※ 予約が完了するまではクリックしないでください。**）

`

// const sendAdInflowInfoToSf = (
//     search: any,
//     me: any,
//     title: string,
// ) => {
//     // URL params
//     const queryParams = new URLSearchParams(search);
//     const utmSource = queryParams.get("utm_source");
//     const utmMedium = queryParams.get("utm_medium");
//     const utmCampaign = queryParams.get("utm_campaign");
//     const utmContent = queryParams.get("utm_content");

//     //user info
//     const lastname = me?.lastname ? me.lastname : "_"
//     const firstname = me?.firstname ? me.firstname : "_"
//     const email = me?.email ? me.email : "placeholder@datamix.co.jp"

//     // sendQuery
//     const userQuery = `?lastname=${encodeURIComponent(lastname)}&firstname=${encodeURIComponent(firstname)}&email=${encodeURIComponent(email)}&title=${encodeURIComponent(title)}`
//     const utmSourceQuery = utmSource ? `&utm_source=${encodeURIComponent(utmSource)}`: ""
//     const utmMediumQuery = utmMedium ? `&utm_medium=${encodeURIComponent(utmMedium)}`: ""
//     const utmCampaignQuery = utmCampaign ? `&utm_campaign=${encodeURIComponent(utmCampaign)}`: ""
//     const utmContentQuery = utmContent ? `&utm_content=${encodeURIComponent(utmContent)}`: ""

//     const sendUrl = `${salesformUrl}${userQuery}${utmSourceQuery}${utmMediumQuery}${utmCampaignQuery}${utmContentQuery}`

//     // info to Salesforce
//     const element = document.createElement("iframe");
//     element.src = "salesforce";
//     element.src = sendUrl;
//     element.width = "1";
//     element.height = "1";
//     document.body.appendChild(element);
// }

const GoogleForm = (props: {
    formInfo: formInfoType,
    eventHandler: () => void,
    classes: any
}) => {
    const { formInfo, eventHandler } = props
    return (
        <div onLoad={eventHandler}>
            <iframe
                id="googleForm"
                src={formInfo.url}
                width={formInfo.width}
                height={formInfo.height}
                frameBorder={"0"}
                marginHeight={0}
                marginWidth={0}
            >
                読み込んでいます…
            </iframe>
        </div>
    )
}

const GoogleCalendar = (props: {
    formInfo: formInfoType,
    eventHandler: () => void,
    classes: any
}) => {
    const { formInfo, eventHandler, classes } = props
    return (
        <Container maxWidth="lg" className={classes.container}>
            <ReactMarkdown source={procedure} />
            <Box
                display="flex"
                justifyContent="flex-end"
            >
                <Fab
                    className={classes.primaryFabFlat}
                    variant="extended"
                    size="medium"
                    color="primary"
                    type="submit"
                    onClick={eventHandler}
                >
                    {"完了"}
                </Fab>
            </Box>
            <iframe
                id="googleCalendar"
                src={formInfo.url}
                width={formInfo.width}
                height={formInfo.height}
                frameBorder={"0"}
                marginHeight={0}
                marginWidth={0}
            >
                読み込んでいます…
            </iframe>
        </Container>
    )
}

const timerexScriptId = "timerex_embed"
const timerexScriptSrc = "https://asset.timerex.net/js/embed.js"

// embed.js は TimerexCalendar() を呼ぶたびに message リスナーを追加し、古いものは外さない。
// SPA 遷移で再マウントすると 1 回の予約で onBookingComplete が複数回呼ばれるため、
// 現在マウント中のハンドラだけを保持し、最初の 1 回で消費する
let timerexBookingCompleteHandler: (() => void) | null = null
const handleTimerexBookingComplete = () => {
    const handler = timerexBookingCompleteHandler
    timerexBookingCompleteHandler = null
    if (handler) {
        handler()
    }
}

const TimerexCalendar = (props: {
    formInfo: formInfoType,
    eventHandler: () => void,
    // ログイン状態・受講生情報の取得が終わるまでは描画しない（自動入力値を確定させてから 1 回だけ描画するため）
    ready: boolean,
    guestName: string,
    guestEmail: string,
}) => {
    const { formInfo, eventHandler, ready, guestName, guestEmail } = props
    useEffect(() => {
        timerexBookingCompleteHandler = eventHandler
        return () => {
            timerexBookingCompleteHandler = null
        }
    }, [eventHandler])
    useEffect(() => {
        if (!ready) {
            return
        }
        // SPA 遷移で再マウントされても毎回ウィジェットを描画する
        const render = () => {
            const w = window as any
            const container = document.getElementById("timerex_calendar")
            if (typeof w.TimerexCalendar !== "function" || !container) {
                return
            }
            // embed.js は呼ぶたびに iframe を追加するので、再描画時は前の iframe を消す
            container.innerHTML = ""
            w.TimerexCalendar({
                // 予約フォームの「名前」「メールアドレス」をマイページの登録情報で自動入力する
                ...(guestName ? { guest_name: guestName } : {}),
                ...(guestEmail ? { guest_email: guestEmail } : {}),
                onBookingComplete: handleTimerexBookingComplete,
            })
        }
        const existing = document.getElementById(timerexScriptId)
        if (existing) {
            render()
            return
        }
        const script = document.createElement("script")
        script.id = timerexScriptId
        script.src = timerexScriptSrc
        script.onload = render
        document.body.appendChild(script)
    }, [formInfo.url, ready, guestName, guestEmail])
    return (
        <div id="timerex_calendar" data-url={formInfo.url}></div>
    )
}


const ProductGoogleTools = (props: any) => {
    const { classes } = props;
    const { userState, setRedirect } = useUserStateContext();
    const { me, getMe, getMeError } = useGetMe();

    const [isFirst, setIsFirst] = useState(true);
    const search = useLocation().search;
    const tag: string = props.match.params.freeType;

    const formInfo = googleform.filter((item) => {
        return item.tag === tag
    })[0]

    const redirectForm = () => {
        if (isFirst) {
            setIsFirst(false);
            return;
        }
        // sendAdInflowInfoToSf(search, me, formInfo.name);
        props.history.push({
            pathname: `/order/completed`,
            state: {
                product: formInfo,
                previous: "OrderCreate",
            },
        });
    };
    // 未ログインなら自動入力なしで描画、ログイン済みなら受講生情報の取得（または失敗）を待ってから描画する
    const timerexReady =
        userState.signedIn === false ||
        (userState.signedIn === true && (Boolean(me?.email) || Boolean(getMeError)))
    const guestName = [me?.last_name, me?.first_name].filter(Boolean).join(" ")
    const guestEmail = me?.email || ""
    // 予約完了後は /order/completed へ遷移せず、TimeRex の完了画面（予約日時・Zoom URL など）をそのまま見せる。
    // 広告成果は GTM の既存カスタムイベント timerex_booking_complete で計測する。
    // booking_tracking_id は Google 広告の注文 ID（重複計上の防止）に使われるので、予約ごとに一意な値を入れる
    const trackTimerexBooking = () => {
        const w = window as any
        w.dataLayer = w.dataLayer || []
        w.dataLayer.push({
            event: "timerex_booking_complete",
            booking_tracking_id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
            productName: formInfo.name,
        })
    }
    const redirectCalendar = () => {
        // sendAdInflowInfoToSf(search, me, formInfo.name);
        props.history.push({
            pathname: `/order/completed`,
            state: {
                product: formInfo,
                previous: "OrderCreate",
            },
        });
    }

    useEffect(() => {
        // replace にして、ブラウザの「戻る」でこのページに戻って再び遷移するのを防ぐ
        if (formInfo?.type === "timerex_link") {
            window.location.replace(formInfo.url)
        }
    }, [formInfo]);

    useEffect(() => {
        // if (userState.signedIn === false) {
        //     setRedirect(window.location.pathname);
        //     props.history.push("/login");
        // } else {
        //     getMe({})
        // }
        if (userState.signedIn === true) {
            getMe({})
        }
    }, [userState, props.history]);

    return (
        <Fragment>
            <Container
                maxWidth="lg"
                className={classes.container2}
            >
                {formInfo ?
                    <Box>
                        <Typography
                            component="h1"
                            variant="h6"
                            className={classes.mb2}
                        >
                            {formInfo.name}
                        </Typography>
                        {formInfo.type === "timerex_link" ?
                            <Typography>
                                {"予約ページへ移動しています。移動しない場合は"}
                                <a href={formInfo.url}>{"こちら"}</a>
                                {"をクリックしてください。"}
                            </Typography> :
                        formInfo.type === "timerex" ?
                            <TimerexCalendar
                                formInfo={formInfo}
                                eventHandler={trackTimerexBooking}
                                ready={timerexReady}
                                guestName={guestName}
                                guestEmail={guestEmail}
                            /> :
                        formInfo.type === "calendar" ?
                            <GoogleCalendar
                                formInfo={formInfo}
                                eventHandler={redirectCalendar}
                                classes={classes}
                            /> :
                            <GoogleForm
                                formInfo={formInfo}
                                eventHandler={redirectForm}
                                classes={classes}
                            />
                        }
                    </Box>
                : <Box>{"URLをお確かめください"}</Box>
                }
            </Container>
        </Fragment>
    )
}

export default withStyles(stylesCommon)(
    withStyles(stylesProduct)(withRouter(ProductGoogleTools))
);
